import { useEffect, useState, startTransition, Suspense } from "react"
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarProvider,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroupContent,
  SidebarGroup,
  SidebarFooter,
  SidebarInset,
  SidebarTrigger,
} from "./ui/sidebar"
import { ErrorBoundary as ErrorBoundaryComponent } from "./Errors"
import { TooltipProvider } from "./ui/tooltip"
import { IconGraph } from "./Icons"
import { Await, Link, NavLink, useLocation } from "react-router"
import { SidebarUserMenu } from "./SidebarUserMenu"
import { BillingContextCookie } from "~/server/cookies.server"
import { BookOpenIcon, Code2Icon, FileTextIcon, Boxes, LogInIcon } from "lucide-react"
import { AppFeature } from "~/server/models/appfeature"
import { BOUNDARIES_DISABLED } from "~/lib/boundaries-disabled"
import { Toaster } from "./ui/toaster"
import { ReleaseAnnouncementBanner } from "./ReleaseAnnouncementBanner"
import { MaintenanceBar } from "./MaintenanceBar"
import { TopBars } from "./TopBars"

const SIDEBAR_VISIT_COUNT_KEY = "sidebar:visit-count"
const SIDEBAR_SESSION_COUNTED_KEY = "sidebar:visit-counted-this-session"
const SIDEBAR_FAMILIARITY_THRESHOLD = 3
const SIDEBAR_COOKIE_NAME = "sidebar:state"
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7 // One week in seconds

function useSidebarDefaultOpen() {
  const [defaultOpen, setDefaultOpen] = useState(true)

  useEffect(() => {
    if (typeof window === "undefined") return

    try {
      const sidebarCookie = document.cookie
        .split(";")
        .find((cookie) => cookie.trim().startsWith(`${SIDEBAR_COOKIE_NAME}=`))

      if (sidebarCookie) {
        const value = sidebarCookie.split("=")[1]?.trim()
        if (value === "false") {
          startTransition(() => {
            setDefaultOpen(false)
          })
        } else if (value === "true") {
          startTransition(() => {
            setDefaultOpen(true)
          })
        }
        return
      }

      if (sessionStorage.getItem(SIDEBAR_SESSION_COUNTED_KEY)) {
        return
      }

      const visitCount = parseInt(
        localStorage.getItem(SIDEBAR_VISIT_COUNT_KEY) || "0",
        10,
      )
      const newVisitCount = visitCount + 1
      localStorage.setItem(SIDEBAR_VISIT_COUNT_KEY, newVisitCount.toString())
      sessionStorage.setItem(SIDEBAR_SESSION_COUNTED_KEY, "true")

      if (newVisitCount >= SIDEBAR_FAMILIARITY_THRESHOLD) {
        startTransition(() => {
          setDefaultOpen(false)
        })
        document.cookie = `${SIDEBAR_COOKIE_NAME}=false; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}; Secure; SameSite=Lax`
      }
    } catch (e) {
      console.warn("Failed to access storage for sidebar state:", e)
    }
  }, [])

  return defaultOpen
}

export default function ConsoleLayout({
  children,
  billingContext,
  roles,
  hasOperatingOrgAccess,
  userAccountCount,
  authed = true,
  showDocsLink = false,
}: {
  children: React.ReactNode
  billingContext: BillingContextCookie
  roles: string[]
  hasOperatingOrgAccess?: boolean
  userAccountCount?: number
  authed?: boolean
  showDocsLink?: boolean | Promise<boolean>
}) {
  const defaultOpen = useSidebarDefaultOpen()

  // Logged-out visitors (public docs) get no sidebar at all — the docs top
  // bar is the only navigation, and the content goes full-width. The
  // TooltipProvider normally comes from SidebarProvider, so it must be
  // provided here explicitly for tooltips in the docs (e.g. mega menu).
  if (!authed) {
    return (
      <TooltipProvider delayDuration={0}>
        <div className="flex min-h-screen w-screen">
          <main className="relative flex w-full flex-1 flex-col">
            {/* API users often read the docs without logging in. */}
            <TopBars>
              <MaintenanceBar />
            </TopBars>
            {children}
          </main>
          <Toaster />
        </div>
      </TooltipProvider>
    )
  }

  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <div className="flex min-h-screen w-screen">
        <ConsoleSidebar
          billingContext={billingContext}
          roles={roles}
          hasOperatingOrgAccess={hasOperatingOrgAccess}
          userAccountCount={userAccountCount}
          authed={authed}
          showDocsLink={showDocsLink}
        />
        <SidebarInset>
          <TopBars>
            <MaintenanceBar />
            <ReleaseAnnouncementBanner
              features={billingContext.enabledFeatures ?? []}
            />
          </TopBars>
          {/* Keeps the sidebar trigger anchored to the page, below the bar. */}
          <div className="relative flex min-w-0 flex-1 flex-col">
            <span className="absolute top-0 left-0 z-[60] ml-1 mt-3">
              <SidebarTrigger className="text-muted-foreground" />
            </span>
            {children}
          </div>
        </SidebarInset>
        <Toaster />
      </div>
    </SidebarProvider>
  )
}

export function ErrorBoundary() {
  return <ErrorBoundaryComponent />
}

const ConsoleSidebar = ({
  billingContext,
  roles,
  hasOperatingOrgAccess,
  userAccountCount,
  authed,
  showDocsLink = false,
}: {
  billingContext: BillingContextCookie
  roles: string[]
  hasOperatingOrgAccess?: boolean
  userAccountCount?: number
  authed: boolean
  showDocsLink?: boolean | Promise<boolean>
}) => {
  const location = useLocation()
  const items = authed ? getSidebarItems(billingContext) : []

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <Link to={authed ? "/" : "/docs"} className="flex items-center gap-2">
          <img
            src="/brand/logo-symbol-dark.svg"
            alt="Pine59"
            className="h-6 w-auto shrink-0"
          />
          <h1 className="text-xl hidden group-data-[state=expanded]:block group-data-[collapsible=icon]:hidden">
            {authed ? "Console" : "Docs"}
          </h1>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => (
                <SidebarMenuItem key={item.label}>
                  <NavLink to={item.to} className="block">
                    {({ isActive }) => (
                      <SidebarMenuButton
                        isActive={isActive}
                        tooltip={item.label}
                      >
                        <item.icon />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    )}
                  </NavLink>
                </SidebarMenuItem>
              ))}
              {authed && (
                // Per-user `public-docs-link` flag (deferred, resolved here so
                // it never blocks the layout loader). Links to the clean
                // `/documentation` redirect, which resolves the secret docs
                // prefix server-side. Plain anchor so the server-side 302 is
                // followed reliably into the separate docs chrome.
                <Suspense fallback={null}>
                  <Await resolve={showDocsLink}>
                    {(enabled) =>
                      enabled ? (
                        <SidebarMenuItem>
                          <a href="/documentation" className="block">
                            <SidebarMenuButton tooltip="Documentation">
                              <BookOpenIcon />
                              <span>Documentation</span>
                            </SidebarMenuButton>
                          </a>
                        </SidebarMenuItem>
                      ) : null
                    }
                  </Await>
                </Suspense>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        {authed ? (
          <SidebarUserMenu
            billingContext={billingContext}
            roles={roles}
            hasOperatingOrgAccess={hasOperatingOrgAccess}
            userAccountCount={userAccountCount}
          />
        ) : (
          <SidebarMenu>
            <SidebarMenuItem>
              <Link
                to={`/login?redirectTo=${encodeURIComponent(location.pathname)}`}
                className="block"
              >
                <SidebarMenuButton tooltip="Log in">
                  <LogInIcon />
                  <span>Log in</span>
                </SidebarMenuButton>
              </Link>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarFooter>
    </Sidebar>
  )
}

type NavItemType = {
  label: string
  icon: React.ComponentType
  to: string
}

const getSidebarItems = (
  billingContext: BillingContextCookie,
): NavItemType[] => {
  // The docs surface lives behind a secret path prefix (`/<secret>/docs/...`).
  // A "Documentation" link is added separately in ConsoleSidebar, gated by the
  // per-user `public-docs-link` PostHog flag, and routed via the
  // `/documentation` redirect so the secret never enters the client bundle.
  const items: NavItemType[] = [
    {
      label: "Datasets",
      icon: FileTextIcon,
      to: "/datasets",
    },
    {
      label: "Data Explorer",
      icon: IconGraph,
      to: "/explore",
    },
    {
      label: "Developer Hub",
      icon: Code2Icon,
      to: "/developer",
    },
  ]

  // Hidden while the Boundaries service is disabled — see `boundaries-disabled.ts`.
  const hasBoundariesAccess =
    !BOUNDARIES_DISABLED &&
    (billingContext.enabledFeatures?.includes(AppFeature.BOUNDARIES) ?? false)
  if (hasBoundariesAccess) {
    items.push({
      label: "Boundaries",
      icon: Boxes,
      to: "/boundaries",
    })
  }

  return items
}
